"use client";

import { useEffect } from "react";

import { useAuth } from "@/shared/auth/hooks";
import { log, setLogUser } from "@/lib/logger";

const CHUNK_RELOAD_KEY = "chunk-error-reload:v1";
const CHUNK_RELOAD_WINDOW_MS = 15_000;

const THROTTLE_WINDOW_MS = 60_000;
const THROTTLE_MAX = 10;
let throttleCount = 0;
let throttleWindowStart = 0;

function shouldThrottle(): boolean {
  const now = Date.now();
  if (now - throttleWindowStart > THROTTLE_WINDOW_MS) {
    throttleWindowStart = now;
    throttleCount = 0;
  }
  throttleCount++;
  return throttleCount > THROTTLE_MAX;
}

function isChunkError(msg: string) {
  return (
    msg.includes("ChunkLoadError") ||
    msg.includes("Failed to load chunk") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg === "Load failed"
  );
}

function tryChunkReload(): boolean {
  try {
    const ts = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (ts && Date.now() - Number(ts) < CHUNK_RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // private mode / quota — reload once, no guard
  }
  window.location.reload();
  return true;
}

export function LogClient() {
  const { user } = useAuth();

  useEffect(() => {
    setLogUser(user?.id ?? null);
  }, [user?.id]);

  useEffect(() => {
    function onError(event: ErrorEvent) {
      if (event.message === "Script error." && !event.filename) return;
      if (isChunkError(event.message || "")) {
        tryChunkReload();
        return;
      }
      if (shouldThrottle()) return;

      log.error(event.message || "window.error", {
        source: event.filename,
        line: event.lineno,
        col: event.colno,
        stack: event.error instanceof Error ? event.error.stack : undefined,
      });
    }

    function onRejection(event: PromiseRejectionEvent) {
      const reason = event.reason;
      const msg = reason instanceof Error ? reason.message : String(reason);
      if (isChunkError(msg)) {
        tryChunkReload();
        return;
      }
      if (shouldThrottle()) return;
      log.error(`unhandledrejection: ${msg}`, {
        stack: reason instanceof Error ? reason.stack : undefined,
      });
    }

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
